//@ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { FormProvider } from 'src/components/hook-form';
import {
  useEpisodeOfCares,
  usePractitionerRoles,
  useValueSet,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import DateTimePicker from 'src/components/DateTimePicker/DateTimePicker';
import { isTimeIncludedInDate } from 'src/utils/dates';
import LoadableAutocomplete from 'src/components/LoadableAutocomplete';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import CancelButton from 'src/careflow/common/CancelButton';
import { isEmpty } from 'lodash';
import { PractitionerRoleWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import { WrappedEpisodeOfCare } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/EpisodeOfCare';

type FormValue = {
  status: ValueSetComposeIncludeConcept;
  start: string;
  end: string;
  careManager: any;
  episodeOfCareType: ValueSetComposeIncludeConcept;
  reasonForStatusChange?: ValueSetComposeIncludeConcept;
};

type Props = {
  patient: WrappedPatient | null;
  episodeOfCare: WrappedEpisodeOfCare | null;
  open: boolean;
  onClose: VoidFunction;
};

export function EpisodeOfCareForm({ patient, episodeOfCare, open, onClose }: Props) {
  const { i18n } = useLocales();

  const { componentsData } = useTenantConfigData();
  const { defaultValues: tenantDefaultValues } = componentsData?.episodeOfCare ?? {};

  const [episodeOfCareStatusVS, { isLoading: isEpisodeOfCareStatusVSLoading }] = useValueSet(
    'episode-of-care-status',
    {
      map: ValueSetWrapper,
    }
  );

  const [practitionerRoles] = usePractitionerRoles({
    filter: { active: true },
    map: PractitionerRoleWrapper,
  });

  const practitionerRoleOptions = useMemo(
    () =>
      practitionerRoles
        .map((x) => ({
          display: x?.getPractitionerName(),
          id: x?.id,
          resourceType: 'PractitionerRole',
        }))
        .filter((x) => !isEmpty(x?.display)),
    [practitionerRoles]
  );

  const EventSchema = Yup.object().shape({
    status: Yup.mixed().required('Status is required'),
    episodeOfCareType: Yup.mixed().required('Type is required'),
    end: Yup.date()
      .nullable()
      .transform((curr, orig) => (moment(orig).isValid() ? curr : null))
      .test('is-greater', 'End date must be greater than start date', function (value) {
        const startDate = moment(this.parent.start);
        const endDate = moment(value);
        if (!startDate.isValid() || !endDate.isValid()) return true;

        return startDate.isSameOrBefore(
          endDate,
          isTimeIncludedInDate(endDate) && isTimeIncludedInDate(startDate) ? undefined : 'date'
        );
      }),
  });
  const [types, { isLoading: isTypesLoading }] = useValueSet('episodeofcare-type', {
    map: ValueSetWrapper,
  });

  const [statusChangeReasonVS, { isLoading: isStatusChangeReasonVSLoading }] = useValueSet(
    'episodeofcare-status-change-reason',
    {
      map: ValueSetWrapper,
    }
  );

  const defaultValues = useMemo(() => {
    const defaultType = types
      ?.asListAll?.()
      ?.find?.(({ code }) => code === tenantDefaultValues?.type?.code);
    const defaultEpisodeOfCare = episodeOfCare?.type?.[0]?.coding?.[0]
      ? episodeOfCare.type[0].coding[0]
      : defaultType
      ? defaultType
      : tenantDefaultValues?.type
      ? tenantDefaultValues?.type
      : null;
    const defaultCareManager = episodeOfCare?.careManager ? episodeOfCare?.careManager : null;
    const defaultStatus = episodeOfCare?.status
      ? episodeOfCareStatusVS
          ?.asListAll?.()
          ?.filter?.((x) => x?.code === episodeOfCare?.status)?.[0]
      : null;

    // Extract reason from statusHistory extension
    let defaultReason: any = undefined;
    if (episodeOfCare?.statusHistory && statusChangeReasonVS) {
      const extensionUrl =
        'https://terminology.patientholistics.app/fhir/StructureDefinition/episodeofcare-status-history-reason';
      const statusHistoryWithReason = [...episodeOfCare.statusHistory]
        .reverse()
        .find((entry: any) => entry?.extension?.some((ext: any) => ext.url === extensionUrl));

      if (statusHistoryWithReason) {
        const reasonExtension = statusHistoryWithReason.extension.find(
          (ext: any) => ext.url === extensionUrl
        );
        const reasonCoding = reasonExtension?.valueCodeableConcept?.coding?.[0];

        if (reasonCoding) {
          defaultReason = statusChangeReasonVS
            ?.asListAll?.()
            ?.find(
              (option) => option.code === reasonCoding.code && option.system === reasonCoding.system
            );
        }
      }
    }

    return {
      episodeOfCareType: defaultEpisodeOfCare,
      careManager: defaultCareManager,
      status: defaultStatus,
      start: episodeOfCare?.period?.start || '',
      end: episodeOfCare?.period?.end || '',
      reasonForStatusChange: defaultReason,
    } as FormValue;
  }, [episodeOfCare, tenantDefaultValues, types, statusChangeReasonVS, episodeOfCareStatusVS]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, defaultValues]);

  const methods = useForm<FormValue>({ resolver: yupResolver(EventSchema), defaultValues });

  const [, { create, update }] = useEpisodeOfCares({ autofetch: false, client: 'indexed' });

  const [isTimeIncluded, setIsTimeIncluded] = useState(false);
  const [isTimeIncludedInEndInitially, setIsTimeIncludedInEndInitially] = useState(false);
  const [isTimeIncludedInStartInitially, setIsTimeIncludedInStartInitially] = useState(false);

  useEffect(() => {
    if (!episodeOfCare) return;
    setIsTimeIncludedInStartInitially(isTimeIncludedInDate(episodeOfCare?.period?.start ?? null));
    setIsTimeIncludedInEndInitially(isTimeIncludedInDate(episodeOfCare?.period?.end ?? null));
  }, [episodeOfCare]);

  const { reset, control, handleSubmit, formState } = methods;

  const onSubmit = async (dataForm: FormValue) => {
    if (episodeOfCare) {
      updateEpisodeOfCare(dataForm);
    } else {
      createEpisodeOfCare(dataForm);
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  const createEpisodeOfCare = async (dataForm: any) => {
    await create({
      resourceType: 'EpisodeOfCare',
      patient: { reference: `Patient/${patient?.id}` },
      managingOrganization: patient?.managingOrganization?.reference
        ? patient?.managingOrganization
        : undefined,
      status: dataForm?.status?.code,
      careManager: dataForm?.careManager?.id
        ? {
            reference: `${dataForm?.careManager?.resourceType}/${dataForm?.careManager?.id}`,
            display: dataForm?.careManager?.display,
          }
        : undefined,
      type: [
        {
          coding: [
            {
              display: dataForm?.episodeOfCareType?.display,
              system: dataForm?.episodeOfCareType?.system,
              code: dataForm?.episodeOfCareType?.code,
            },
          ],
          text: dataForm?.episodeOfCareType?.display,
        },
      ],
      ...((dataForm?.start || dataForm?.end) && {
        period: {
          ...(dataForm?.start && { start: dataForm?.start }),
          ...(dataForm?.end && { end: dataForm?.end }),
        },
      }),
      statusHistory: [
        {
          status: dataForm?.status?.code,
          period: {
            start: moment().toISOString(),
          },
        },
      ],
    })
      .then(() => {
        handleClose();
      })
      .catch(() => {});
  };

  const updateEpisodeOfCare = async (dataForm: any) => {
    const currentStatus = episodeOfCare?.status;
    const newStatus = dataForm?.status?.code;
    const statusChanged = !!currentStatus && !!newStatus && currentStatus !== newStatus;
    let statusHistory = [];
    if (statusChanged) {
      const now = moment().toISOString();
      statusHistory = [...(episodeOfCare?.statusHistory || [])];
      const lastIndex = statusHistory.length - 1;
      if (lastIndex >= 0) {
        statusHistory[lastIndex] = {
          ...statusHistory[lastIndex],
          period: { ...statusHistory[lastIndex].period, end: now },
        };
      }
      const statusHistoryEntry: any = { status: newStatus, period: { start: now } };
      const reason = dataForm?.reasonForStatusChange;
      if (reason?.code) {
        statusHistoryEntry.extension = [
          {
            url: 'https://terminology.patientholistics.app/fhir/StructureDefinition/episodeofcare-status-history-reason',
            valueCodeableConcept: {
              coding: [
                {
                  system: reason.system || 'http://snomed.info/sct',
                  code: reason.code,
                  display: reason.display,
                },
              ],
              text: reason.display,
            },
          },
        ];
      }
      statusHistory.push(statusHistoryEntry);
    }

    const { includedResources, careManager, ...rest } = episodeOfCare ?? {};

    const updatePayload: any = {
      ...rest,
      id: episodeOfCare?.id,
      resourceType: 'EpisodeOfCare',
      patient: { reference: `Patient/${patient?.id}` },
      managingOrganization: patient?.managingOrganization?.reference
        ? patient?.managingOrganization
        : undefined,
      status: dataForm?.status?.code,
      careManager: dataForm?.careManager?.id
        ? {
            reference: `${dataForm?.careManager?.resourceType}/${dataForm?.careManager?.id}`,
            display: dataForm?.careManager?.display,
          }
        : careManager?.reference
        ? careManager
        : undefined,
      type: [
        {
          coding: [
            {
              display: dataForm?.episodeOfCareType?.display,
              system: dataForm?.episodeOfCareType?.system,
              code: dataForm?.episodeOfCareType?.code,
            },
          ],
          text: dataForm?.episodeOfCareType?.display,
        },
      ],
    };

    if (dataForm?.start || dataForm?.end) {
      updatePayload.period = {
        ...(dataForm?.start && { start: moment(dataForm?.start).toISOString() }),
        ...(dataForm?.end && { end: moment(dataForm?.end).toISOString() }),
      };
    }

    if (statusHistory.length > 0) {
      updatePayload.statusHistory = statusHistory;
    }

    await update(updatePayload)
      .then(() => {
        handleClose();
      })
      .catch((err) => {
        console.log(err);
      });
  };
  return (
    <Dialog open={open} fullWidth={true} maxWidth="md">
      <DialogTitle>
        {episodeOfCare
          ? i18n('patients.details.episodeOfCare.titleEditEpisodeOfCare', 'crs')
          : i18n('patients.details.episodeOfCare.titleAddEpisodeOfCare', 'crs')}
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ m: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <Controller
                  name={'episodeOfCareType'}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const { onChange, onBlur, value } = field;
                    return (
                      <>
                        <LoadableAutocomplete
                          fullWidth
                          label={'Type*'}
                          isLoading={isTypesLoading}
                          value={value}
                          onChange={(_, newValue) => {
                            onChange(newValue);
                          }}
                          error={error?.message}
                          onBlur={onBlur}
                          options={types?.asListAll?.() ?? []}
                          getOptionLabel={({ display }: ValueSetComposeIncludeConcept) =>
                            display ?? ''
                          }
                        />
                      </>
                    );
                  }}
                />

                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const { onChange, onBlur, value } = field;
                    return (
                      <>
                        <LoadableAutocomplete
                          fullWidth
                          label={'Status*'}
                          isLoading={isEpisodeOfCareStatusVSLoading}
                          value={value}
                          onChange={(_, newValue) => {
                            onChange(newValue);
                          }}
                          error={error?.message}
                          onBlur={onBlur}
                          options={episodeOfCareStatusVS?.asListAll?.() ?? []}
                          getOptionLabel={({ display }: ValueSetComposeIncludeConcept) =>
                            display ?? ''
                          }
                        />
                      </>
                    );
                  }}
                />

                {episodeOfCare && (
                  <Controller
                    name="reasonForStatusChange"
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                      const { onChange, onBlur, value } = field;

                      return (
                        <>
                          <LoadableAutocomplete
                            fullWidth
                            label={'Reason for Status Change'}
                            isLoading={isStatusChangeReasonVSLoading}
                            value={value}
                            onChange={(_, newValue) => {
                              onChange(newValue);
                            }}
                            error={error?.message}
                            onBlur={onBlur}
                            options={statusChangeReasonVS?.asListAll?.() ?? []}
                            getOptionLabel={({ display }: ValueSetComposeIncludeConcept) =>
                              display ?? ''
                            }
                          />
                        </>
                      );
                    }}
                  />
                )}

                <Controller
                  name={'careManager'}
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      value={field.value || null}
                      onChange={(event, newValue) => field.onChange(newValue)}
                      onBlur={field.onBlur}
                      options={practitionerRoleOptions}
                      getOptionLabel={({ display }) => display ?? ''}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) =>
                        options.filter((option) =>
                          option.display.toLowerCase().includes(state.inputValue.toLowerCase())
                        )
                      }
                      renderOption={(props, option, { index }) => (
                        <li {...props} key={`${option.id}-${index}`}>
                          {option.display}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={i18n('patients.details.episodeOfCare.careManager', 'crs')}
                        />
                      )}
                    />
                  )}
                />

                <Typography sx={{ ml: 1, mt: 0.7, py: 2 }}>
                  {i18n('patients.details.episodeOfCare.period', 'crs')}
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: 1 }}>
                  <Controller
                    name="start"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <DateTimePicker
                          {...field}
                          textFieldProps={{ fullWidth: true }}
                          label={i18n('patients.details.episodeOfCare.start', 'crs')}
                          isTimeIncluded={
                            episodeOfCare ? isTimeIncludedInStartInitially : isTimeIncluded
                          }
                          onTimeInclusionChange={setIsTimeIncluded}
                          isTimeOptional
                          errorMessage={error?.message}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="end"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <DateTimePicker
                        {...field}
                        textFieldProps={{ fullWidth: true }}
                        isTimeIncluded={
                          episodeOfCare ? isTimeIncludedInEndInitially : isTimeIncluded
                        }
                        label={i18n('patients.details.episodeOfCare.end', 'crs')}
                        isTimeOptional
                        errorMessage={error?.message}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <Stack spacing={2} alignItems="center">
            <DialogActions>
              <Box sx={{ flexGrow: 1 }} />

              <CancelButton onClick={handleClose}>{i18n('cancel')}</CancelButton>

              <Button disabled={formState.isSubmitting} variant="contained" type="submit">
                {i18n('saveChanges')}
              </Button>
            </DialogActions>
          </Stack>
        </Card>
      </FormProvider>
    </Dialog>
  );
}
